import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Loader2, Palette, Scissors, Droplet, ArrowRight, Sparkles } from "lucide-react";
import { ScoreBar, prettyEnum } from "./beautyUi";

export default function BeautyReport() {
  const { scanId } = useParams();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/beauty/scans/${scanId}`);
        setScan(data.scan);
      } finally {
        setLoading(false);
      }
    })();
  }, [scanId]);

  if (loading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!scan) return <div className="max-w-5xl mx-auto p-16 text-center">Scan not found.</div>;

  const a = scan.analysis || {};
  const face = a.face || {}, skin = a.skin || {}, hair = a.hair || {}, beard = a.beard || {}, scalp = a.scalp || {};
  const scores = a.overall_scores || {};
  const beardApplicable = beard.applicable;
  const date = new Date(scan.created_at).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14" data-testid="beauty-report">
      <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> AI Scan · {date}</p>
      <h1 className="editorial-h1 mt-3">Your beauty brief.</h1>
      {a.summary && <p className="font-serif text-2xl italic mt-4 leading-snug max-w-3xl">&ldquo;{a.summary}&rdquo;</p>}

      {/* Scores */}
      <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-10 grid sm:grid-cols-3 gap-6 border border-border p-6">
        <ScoreBar label="Skin Health"   value={scores.skin_health}   testId="report-score-skin" />
        <ScoreBar label="Hair Health"   value={scores.hair_health}   testId="report-score-hair" />
        {beardApplicable && <ScoreBar label="Beard Health" value={scores.beard_health} testId="report-score-beard" />}
      </motion.section>

      <div className="mt-12 grid lg:grid-cols-2 gap-8">
        <Card title="Face">
          <Row label="Face shape" value={prettyEnum(face.face_shape)} />
          <Row label="Skin tone" value={prettyEnum(face.skin_tone)} />
          <Row label="Undertone" value={prettyEnum(face.skin_undertone)} />
          <Row label="Symmetry" value={face.facial_symmetry_score ? `${face.facial_symmetry_score}/100` : "—"} />
          {face.notes && <p className="text-xs text-muted-foreground mt-3">{face.notes}</p>}
        </Card>

        <Card title="Skin">
          <Row label="Skin type" value={prettyEnum(skin.skin_type)} />
          {(skin.concerns_detected || []).length > 0 && (
            <div className="mt-2">
              <p className="eyebrow mb-2">Concerns</p>
              <div className="flex flex-wrap gap-1.5">
                {skin.concerns_detected.map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-[0.16em] border border-border px-2 py-1">{prettyEnum(c)}</span>
                ))}
              </div>
            </div>
          )}
          {(skin.top_priorities || []).length > 0 && (
            <div className="mt-4">
              <p className="eyebrow mb-2">Focus first</p>
              <ul className="text-sm space-y-1 list-disc list-inside">
                {skin.top_priorities.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
        </Card>

        <Card title="Hair">
          <Row label="Density" value={prettyEnum(hair.hair_density)} />
          <Row label="Thickness" value={prettyEnum(hair.hair_thickness)} />
          <Row label="Texture" value={prettyEnum(hair.hair_texture)} />
          <Row label="Curl pattern" value={hair.curl_pattern && hair.curl_pattern !== "unknown" ? hair.curl_pattern.toUpperCase() : "—"} />
          <Row label="Volume" value={prettyEnum(hair.volume)} />
          <Row label="Hairline" value={prettyEnum(hair.hairline_state)} />
          {(hair.concerns || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {hair.concerns.map((c) => (
                <span key={c} className="text-[10px] uppercase tracking-[0.16em] border border-border px-2 py-1">{prettyEnum(c)}</span>
              ))}
            </div>
          )}
        </Card>

        {beardApplicable && (
          <Card title="Beard">
            <Row label="Density" value={prettyEnum(beard.density)} />
            <Row label="Growth" value={prettyEnum(beard.growth_pattern)} />
            <Row label="Symmetry" value={beard.symmetry_score ? `${beard.symmetry_score}/100` : "—"} />
            {(beard.patchiness_areas || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {beard.patchiness_areas.map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-[0.16em] border border-border px-2 py-1">{prettyEnum(c)}</span>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card title="Scalp">
          <Row label="State" value={prettyEnum(scalp.state)} />
          <Row label="Buildup" value={prettyEnum(scalp.buildup)} />
          {scalp.notes && <p className="text-xs text-muted-foreground mt-3">{scalp.notes}</p>}
        </Card>
      </div>

      {/* Next steps */}
      <section className="mt-14" data-testid="beauty-report-next-steps">
        <p className="eyebrow">Where to next</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-4">
          <NextLink to="/beauty/makeup"     icon={Palette}  label="Makeup Studio" desc="Palettes matched to your scan" testId="report-next-makeup" />
          <NextLink to="/beauty/hair-beard" icon={Scissors} label="Hair & Beard"  desc="Styles for your face shape"    testId="report-next-hair" />
          <NextLink to="/beauty/care"       icon={Droplet}  label="Care Point"    desc="Skin · hair · scalp routine"    testId="report-next-care" />
        </div>
      </section>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-border p-6">
      <p className="eyebrow">{title}</p>
      <div className="mt-4 space-y-2 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border pb-2">
      <p className="eyebrow">{label}</p>
      <p className="text-sm">{value}</p>
    </div>
  );
}

function NextLink({ to, icon: Icon, label, desc, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group border border-border p-5 hover:border-foreground transition-all duration-300 hover:-translate-y-1 block"
    >
      <div className="h-10 w-10 inline-flex items-center justify-center bg-muted group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
        <Icon size={18} strokeWidth={1.4} />
      </div>
      <p className="font-serif text-xl mt-3">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
      <ArrowRight size={14} className="mt-3 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
    </Link>
  );
}
